/**
 * Terms of Use — EN (courtesy translation).
 * The French version prevails.
 */
export const termsEn = {
  eyebrow: 'Legal documents',
  title: 'Terms of Use',
  subtitle:
    "These Terms of Use govern access to and use of the openmeta.tech website and associated services provided by OPENMETA. By creating an account or using the services, the user accepts these terms in full.",
  updated: '8 September 2026',
  seoTitle: 'Terms of Use — OpenMeta',
  seoDescription:
    "OpenMeta Terms of Use: creator accounts, Basic and Pro subscriptions, no-code app publishing, moderation, intellectual property, French law.",
  sections: [
    {
      id: 'publisher',
      title: 'Publisher',
      blocks: [
        {
          type: 'p',
          content:
            "The openmeta.tech website ('OpenMeta' or 'the Service') is published by OPENMETA, a French simplified joint-stock company (SAS) with share capital of €40,000, registered with the Alençon Trade and Companies Register under number 978 390 813, whose registered office is 5037 Chemin de la Veillotière, 61330 Ceaucé, France.",
        },
        {
          type: 'p',
          content:
            "European identification number: FR6101.978390813.\nContact: support@openmeta.fr\nPhone: +33 2 59 16 64 45",
        },
      ],
    },
    {
      id: 'purpose',
      title: 'Purpose of the Service',
      blocks: [
        { type: 'p', content: 'OpenMeta is a no-code and AI platform that allows users to:' },
        {
          type: 'ul',
          content: [
            "Generate application concepts from a free-form description ('the Generator').",
            "Explore interactive mockups illustrating these concepts ('the Prototype').",
            "Assemble and publish real mini-applications from configurable blocks: content page, AI chatbot and form ('the Builder').",
            'Host and serve published apps at dedicated public URLs.',
          ],
        },
        {
          type: 'p',
          content:
            "OpenMeta is a creation tool: the user is free with the content they generate and publish, subject to compliance with these Terms, the law and third-party rights.",
        },
      ],
    },
    {
      id: 'accounts',
      title: 'User accounts',
      blocks: [
        {
          type: 'p',
          content:
            "Access to the creator space requires an account created with a valid email address and a password of at least 8 characters. The user declares that they are at least 18 years old and legally capable of entering into a contract.",
        },
        {
          type: 'p',
          content:
            "The user is solely responsible for the confidentiality of their credentials and for any activity performed from their account. Any suspicious use must be reported without delay to support@openmeta.fr.",
        },
        {
          type: 'p',
          content:
            "OPENMETA reserves the right to suspend or delete any account in the event of a breach of these Terms, without such action giving rise to compensation.",
        },
      ],
    },
    {
      id: 'subscriptions',
      title: 'Subscriptions and payment',
      blocks: [
        { type: 'p', content: 'Publishing features require an active subscription. Two plans are offered:' },
        {
          type: 'ul',
          content: [
            'Basic plan: €29 excl. VAT per month, 1 concurrently published app, unlimited drafts.',
            'Pro plan: €290 excl. VAT per month, up to 10 concurrently published apps, unlimited drafts, priority support.',
          ],
        },
        {
          type: 'p',
          content:
            'Annual billing is available with a 20% discount on the equivalent monthly rate. Prices are shown excluding VAT; applicable VAT is added where required by law.',
        },
        {
          type: 'p',
          content:
            "Payments are processed by Stripe, a specialized online payment provider. OPENMETA never receives the full card number. Monthly and annual subscriptions renew automatically. Cancellation can be made at any time from the creator space or by email to support@openmeta.fr; it takes effect at the end of the current period, with no pro-rata refund.",
        },
        {
          type: 'p',
          content:
            "In accordance with Article L.221-28 of the French Consumer Code, users who accept immediate performance of the Service acknowledge that they waive their right of withdrawal for services fully performed before the end of the withdrawal period.",
        },
      ],
    },
    {
      id: 'creator-content',
      title: 'Creator content and license',
      blocks: [
        {
          type: 'p',
          content:
            "The user retains ownership of the content they create, enter and publish through OpenMeta ('Creator Content'). By publishing it at openmeta.tech/app/… or sharing it via the Service, the user grants OPENMETA a non-exclusive, worldwide, royalty-free license limited to hosting, display, technical reproduction and distribution strictly necessary for the Service to operate.",
        },
        {
          type: 'p',
          content:
            "The user warrants that they hold all necessary rights over the Creator Content they publish, that they do not infringe third-party rights (copyright, trademark, privacy, image), and that they comply with applicable law.",
        },
        { type: 'p', content: 'The following are notably prohibited, without this list being exhaustive:' },
        {
          type: 'ul',
          content: [
            'Unlawful, infringing, defamatory, insulting, discriminatory content or content inciting hatred or violence.',
            'Deceptive, fraudulent content, or constituting phishing, spam or manipulation.',
            "Medical, legal, financial or tax content presented as personalized professional advice without a clear disclaimer stating otherwise.",
            'Pornographic, violent or content inappropriate for general audiences.',
            'Content involving collection of personal data without a legal basis or without informing the persons concerned.',
          ],
        },
      ],
    },
    {
      id: 'moderation',
      title: 'Moderation',
      blocks: [
        {
          type: 'p',
          content:
            "OPENMETA reserves the right, at any time and without notice, to unpublish, suspend or remove any app or content that violates these Terms, the law, third-party rights or the legitimate interest of the Service.",
        },
        {
          type: 'p',
          content:
            "When moderated, the app is automatically set to 'draft' and publishing is blocked until an administrator lifts the flag. The reason for moderation is visible to the creator in their studio.",
        },
        {
          type: 'p',
          content:
            'Users may contest a moderation decision at any time by writing to support@openmeta.fr.',
        },
      ],
    },
    {
      id: 'ai',
      title: 'AI-assisted features',
      blocks: [
        {
          type: 'p',
          content:
            'OpenMeta offers generative features (concept generation, prototypes, chatbots) based on third-party AI models. The results produced are provided for information purposes only and may contain errors, approximations or biases.',
        },
        {
          type: 'note',
          content:
            "OpenMeta's AI features do not constitute personalized medical, legal, financial, accounting, tax or professional advice. Any decision based on these outputs must be verified by a qualified human professional.",
        },
        {
          type: 'p',
          content:
            "The processing of data sent to these providers is governed by our Privacy Policy (see 'Processing by artificial intelligence').",
        },
      ],
    },
    {
      id: 'ip',
      title: 'Intellectual property',
      blocks: [
        {
          type: 'p',
          content:
            "All elements making up the Service (branding, code, interfaces, logos, trademarks, texts, illustrations, sounds) are the exclusive property of OPENMETA or its partners and are protected by intellectual property law.",
        },
        {
          type: 'p',
          content:
            "Any reproduction, representation, modification, publication or adaptation of all or part of the Service, by any means whatsoever, is strictly prohibited without OPENMETA's prior written consent.",
        },
        {
          type: 'p',
          content:
            "Use of the Service does not grant the user any ownership rights over OpenMeta's elements.",
        },
      ],
    },
    {
      id: 'availability',
      title: 'Availability and updates',
      blocks: [
        {
          type: 'p',
          content:
            'OPENMETA strives to keep the Service accessible without interruption but does not guarantee continuous availability. The Service may be temporarily suspended for maintenance, updates, technical incidents or force majeure.',
        },
        {
          type: 'p',
          content:
            'OPENMETA may modify the features, parameters and pricing of the Service, subject to reasonable prior notice for any substantial change affecting an active subscription.',
        },
      ],
    },
    {
      id: 'liability',
      title: 'Limitation of liability',
      blocks: [
        {
          type: 'p',
          content:
            'To the extent permitted by law, OPENMETA shall not be liable for indirect damages (loss of data, loss of opportunity, reputational harm, commercial loss) resulting from use of or inability to use the Service.',
        },
        {
          type: 'p',
          content:
            "OPENMETA is not liable for Creator Content published by users or for interactions between users and visitors of published apps.",
        },
        {
          type: 'p',
          content:
            "OPENMETA's total liability, all damages combined, shall not exceed the amount excluding VAT actually paid by the user for their subscription during the twelve (12) months preceding the triggering event.",
        },
      ],
    },
    {
      id: 'data',
      title: 'Personal data',
      blocks: [
        {
          type: 'p',
          content:
            "Processing of personal data by OPENMETA is described in the Privacy Policy, available at /confidentialite. By creating an account, the user acknowledges having read it.",
        },
        {
          type: 'p',
          content:
            'Questions regarding personal data can be sent to support@openmeta.fr.',
        },
      ],
    },
    {
      id: 'law',
      title: 'Governing law and jurisdiction',
      blocks: [
        {
          type: 'p',
          content:
            "These Terms are governed by French law. In the absence of amicable resolution, any dispute relating to their interpretation or performance shall be submitted to the competent courts within the jurisdiction of OPENMETA's registered office, subject to mandatory provisions applicable to consumers.",
        },
      ],
    },
    {
      id: 'contact',
      title: 'Contact',
      blocks: [
        {
          type: 'p',
          content:
            'OPENMETA\n5037 Chemin de la Veillotière\n61330 Ceaucé — France\nPhone: +33 2 59 16 64 45\nEmail: support@openmeta.fr',
        },
      ],
    },
  ],
};
